import { api } from "./api";
import type {
  CVConfigData,
  CvSectionId,
  ProfilePrivacySettings,
  SaveProfilePrivacyInput,
  ProfilePrivateInfo,
  SmartCVPublicDocument,
} from "@tabbio/types";
import type { CVProfile, CVSkillGroup, CVConfig } from "./cv-mock-data";
import {
  smartDocumentToCvProfile,
  normalizePublicCvConfig,
} from "./cv-public-document";
import type { SearchArea } from "./search-area-types";
import {
  cvWriteRequestArgs,
  readResponseRevision,
  reportCvReadRevision,
  settleCvWriteResponse,
  type CvWriteOptions,
} from "./cv-write-coordinator";

export interface ProfileFeaturedProject {
  id: string;
  title: string;
  description?: string | null;
  image?: string | null;
  link?: string | null;
  technologies: string[];
  order?: number;
}

export interface ProfileServiceOffer {
  id: string;
  title: string;
  description: string;
  basePrice?: number | null;
  deliveryDays?: number | null;
  deliverables: string[];
  order: number;
}

export type ProfileResponse = CVProfile & {
  cvConfig?: CVConfig;
  privateInfo?: ProfilePrivateInfo;
  privacy?: ProfilePrivacySettings;
  sectionsConfig?: { order: CvSectionId[] } | null;
  projects?: ProfileFeaturedProject[];
  serviceOffers?: ProfileServiceOffer[];
  /**
   * True when this profile was resolved from a single shared CV (a CV-scoped
   * delegated grant) rather than the owner's whole profile. The UI uses it to
   * hide affordances that have no tailored-CV equivalent (job preferences,
   * open-to-work, cover image).
   */
  cvScoped?: boolean;
};

export type ProfileTarget = string | null | undefined;

type ResolvedCvProfileDocument = SmartCVPublicDocument & {
  cvConfig?: CVConfigData | null;
  privacy?: ProfilePrivacySettings;
  sectionsConfig?: { order: CvSectionId[] } | null;
  cvScoped?: boolean;
};

function profilePath(target: ProfileTarget, suffix = "") {
  const normalized = typeof target === "string" ? target.trim() : "";
  if (!normalized) return `/api/profile${suffix}`;
  return `/api/cv/${encodeURIComponent(normalized)}/profile${suffix}`;
}

// ─── Fetch Full Profile (CVProfile shape + cvConfig) ──────────

export async function fetchProfile(
  target?: ProfileTarget,
): Promise<ProfileResponse> {
  const res = await api.get<ProfileResponse | SmartCVPublicDocument>(
    profilePath(target),
  );
  if (res.error) throw new Error(res.error.message);
  // B02: a CV target answers with the revision of this read (meta.revision);
  // the main profile is not a CV and reports nothing.
  reportCvReadRevision(target, readResponseRevision({ data: null, meta: res.meta }));
  const data = res.data;
  if (!data) return {} as ProfileResponse;
  // A CV-scoped delegated grant resolves to a SmartCVPublicDocument (the shared
  // CV) instead of the whole-profile shape. Map it to the CVProfile the editor
  // expects so every reused /cv screen renders and saves unchanged.
  if (isResolvedCvDocument(data)) {
    const document = data as ResolvedCvProfileDocument;
    return {
      ...smartDocumentToCvProfile(document),
      cvConfig: normalizePublicCvConfig(document.cvConfig),
      privacy: document.privacy,
      sectionsConfig: document.sectionsConfig ?? null,
      cvScoped: document.cvScoped ?? isDelegatedProfileTarget(target),
    };
  }
  return data as ProfileResponse;
}

function isDelegatedProfileTarget(target: ProfileTarget) {
  const normalized = typeof target === "string" ? target.trim() : "";
  return normalized.startsWith("grant:") || normalized.startsWith("access:");
}

function isResolvedCvDocument(
  data: ProfileResponse | SmartCVPublicDocument,
): data is SmartCVPublicDocument {
  // A resolved CV document carries plural `experiences` AND a `cv` metadata
  // object; the whole-profile shape has neither (it uses singular `experience`
  // and no `cv`). Requiring both avoids misclassifying a whole-profile response
  // that happens to grow an `experiences`-shaped field.
  const doc = data as Partial<SmartCVPublicDocument>;
  return Array.isArray(doc.experiences) && "cv" in data;
}

// ─── Update Profile Scalars (header, summary) ─────────────────

export async function updateProfileFields(
  patch: {
    name?: string;
    headline?: string;
    summary?: string;
    location?: string;
    contactEmail?: string;
    phone?: string;
    linkedin?: string;
    website?: string;
    github?: string;
    dribbble?: string;
    behance?: string;
    avatarUrl?: string | null;
    coverImageUrl?: string | null;
  },
  target?: ProfileTarget,
  write?: CvWriteOptions,
): Promise<void> {
  const res = await api.patch(profilePath(target), patch, ...cvWriteRequestArgs(write));
  settleCvWriteResponse(res, write);
}

// ─── Experience ────────────────────────────────────────────────

export async function upsertExperience(
  data: {
    id?: string;
    company: string;
    title: string;
    location?: string;
    locationType?: "Remote" | "On-site" | "Hybrid";
    employmentType?: string;
    startDate: string | null;
    endDate?: string | null;
    current?: boolean;
    description?: string;
    achievements?: string[];
    order?: number;
  },
  target?: ProfileTarget,
  write?: CvWriteOptions,
) {
  const res = await api.put(
    profilePath(target, "/experience"),
    data,
    ...cvWriteRequestArgs(write),
  );
  return settleCvWriteResponse(res, write);
}

export async function deleteExperience(
  id: string,
  target?: ProfileTarget,
  write?: CvWriteOptions,
): Promise<void> {
  const res = await api.delete(
    profilePath(target, `/experience/${id}`),
    ...cvWriteRequestArgs(write),
  );
  settleCvWriteResponse(res, write);
}

// ─── Education ─────────────────────────────────────────────────

export async function upsertEducation(
  data: {
    id?: string;
    school: string;
    degree?: string;
    fieldOfStudy?: string;
    startDate?: string | null;
    endDate?: string | null;
    grade?: string;
    description?: string;
    order?: number;
  },
  target?: ProfileTarget,
  write?: CvWriteOptions,
) {
  const res = await api.put(
    profilePath(target, "/education"),
    data,
    ...cvWriteRequestArgs(write),
  );
  return settleCvWriteResponse(res, write);
}

export async function deleteEducation(
  id: string,
  target?: ProfileTarget,
  write?: CvWriteOptions,
): Promise<void> {
  const res = await api.delete(
    profilePath(target, `/education/${id}`),
    ...cvWriteRequestArgs(write),
  );
  settleCvWriteResponse(res, write);
}

// ─── Certification ─────────────────────────────────────────────

export async function upsertCertification(
  data: {
    id?: string;
    name: string;
    issuer: string;
    issueDate?: string | null;
    expirationDate?: string | null;
    credentialId?: string;
    credentialUrl?: string;
    order?: number;
  },
  target?: ProfileTarget,
  write?: CvWriteOptions,
) {
  const res = await api.put(
    profilePath(target, "/certification"),
    data,
    ...cvWriteRequestArgs(write),
  );
  return settleCvWriteResponse(res, write);
}

export async function deleteCertification(
  id: string,
  target?: ProfileTarget,
  write?: CvWriteOptions,
): Promise<void> {
  const res = await api.delete(
    profilePath(target, `/certification/${id}`),
    ...cvWriteRequestArgs(write),
  );
  settleCvWriteResponse(res, write);
}

// ─── Career Highlight ──────────────────────────────────────────

export async function upsertHighlight(
  data: {
    id?: string;
    title: string;
    description?: string;
    order?: number;
  },
  target?: ProfileTarget,
  write?: CvWriteOptions,
) {
  const res = await api.put(
    profilePath(target, "/highlight"),
    data,
    ...cvWriteRequestArgs(write),
  );
  return settleCvWriteResponse(res, write);
}

export async function deleteHighlight(
  id: string,
  target?: ProfileTarget,
  write?: CvWriteOptions,
): Promise<void> {
  const res = await api.delete(
    profilePath(target, `/highlight/${id}`),
    ...cvWriteRequestArgs(write),
  );
  settleCvWriteResponse(res, write);
}

// ─── Projects ────────────────────────────────────────────────────

export async function upsertProject(
  data: {
    id?: string;
    title: string;
    description?: string;
    image?: string;
    link?: string;
    technologies?: string[];
    order?: number;
  },
  target?: ProfileTarget,
  write?: CvWriteOptions,
) {
  const res = await api.put<ProfileFeaturedProject>(
    profilePath(target, "/project"),
    data,
    ...cvWriteRequestArgs(write),
  );
  return settleCvWriteResponse(res, write);
}

export async function deleteProject(
  id: string,
  target?: ProfileTarget,
  write?: CvWriteOptions,
): Promise<void> {
  const res = await api.delete(
    profilePath(target, `/project/${encodeURIComponent(id)}`),
    ...cvWriteRequestArgs(write),
  );
  settleCvWriteResponse(res, write);
}

// ─── Service Offers ──────────────────────────────────────────────

export async function upsertServiceOffer(data: {
  id?: string;
  title: string;
  description?: string;
  basePrice?: number | null;
  deliveryDays?: number | null;
  deliverables?: string[];
  order?: number;
}) {
  const res = await api.put("/api/profile/service-offer", data);
  if (res.error) throw new Error(res.error.message);
  return res.data;
}

export async function deleteServiceOffer(id: string): Promise<void> {
  const res = await api.delete(`/api/profile/service-offer/${id}`);
  if (res.error) throw new Error(res.error.message);
}

// ─── Skills ────────────────────────────────────────────────────

export async function saveSkillGroups(
  skillGroups: CVSkillGroup[],
  target?: ProfileTarget,
  write?: CvWriteOptions,
): Promise<void> {
  const res = await api.put(
    profilePath(target, "/skills"),
    { skillGroups },
    ...cvWriteRequestArgs(write),
  );
  settleCvWriteResponse(res, write);
}

// ─── Languages ─────────────────────────────────────────────────

export async function saveLanguages(
  languages: Array<{ language: string; proficiency: string }>,
  target?: ProfileTarget,
  write?: CvWriteOptions,
): Promise<void> {
  const res = await api.put(
    profilePath(target, "/languages"),
    { languages },
    ...cvWriteRequestArgs(write),
  );
  settleCvWriteResponse(res, write);
}

// ─── Private Info (demographic / eligibility) ──────────────────

export async function savePrivateInfo(
  info: ProfilePrivateInfo,
  target?: ProfileTarget,
): Promise<void> {
  const res = await api.put(profilePath(target, "/private-info"), info);
  if (res.error) throw new Error(res.error.message);
}

// ─── Job Preferences ───────────────────────────────────────────

export async function saveJobPreferences(
  prefs: {
    openToWork: boolean;
    jobTitles?: string[];
    locationTypes?: string[];
    preferredLocations?: string[];
    employmentTypes?: string[];
    searchAreas?: SearchArea[];
    relocation?: string;
    relocationAreas?: string[];
    noticePeriod?: string;
  },
  target?: ProfileTarget,
): Promise<void> {
  const res = await api.put(profilePath(target, "/job-preferences"), prefs);
  if (res.error) throw new Error(res.error.message);
}

// ─── CV Config ─────────────────────────────────────────────────

export async function saveCvConfig(
  config: CVConfig,
  target?: ProfileTarget,
  write?: CvWriteOptions,
): Promise<void> {
  const res = await api.put(
    profilePath(target, "/cv-config"),
    config,
    ...cvWriteRequestArgs(write),
  );
  settleCvWriteResponse(res, write);
}

export async function saveSectionsConfig(
  config: { order: CvSectionId[] } | null,
  target?: ProfileTarget,
  write?: CvWriteOptions,
): Promise<void> {
  const res = await api.put(
    profilePath(target, "/sections-config"),
    config,
    ...cvWriteRequestArgs(write),
  );
  settleCvWriteResponse(res, write);
}

// ─── Privacy ───────────────────────────────────────────────────

export async function saveProfilePrivacy(
  privacy: SaveProfilePrivacyInput,
): Promise<ProfilePrivacySettings> {
  const res = await api.put<{ privacy: ProfilePrivacySettings }>(
    "/api/profile/privacy",
    privacy,
  );
  if (res.error) throw new Error(res.error.message);
  if (!res.data?.privacy) throw new Error("Could not save privacy settings");
  return res.data.privacy;
}
